import { useCallback } from 'react';

import { usePaymentContext } from 'features/payment';
import { PaymentRequestError, RequestPaymentFn, paymentErrorType } from 'features/payment/types';
import { isPayPal, isVenmo } from 'features/payment/utils';

import { getConfigValue } from 'utils/environment';
import { logger } from 'utils/logger';

import { useBraintreeInitialization } from './use-braintree-initialization.web';

export enum FlowType {
  /**
   * Used to store the payment method for future use, ie subscriptions
   */
  Vault = 'vault',

  /**
   * Used for one-time checkout
   */
  Checkout = 'checkout',
}

export enum Intent {
  /**
   * Submits the transaction for authorization but not settlement.
   */
  Authorize = 'authorize',

  /**
   * Validates the transaction without an authorization (i.e. without holding funds).
   * Useful for authorizing and capturing funds up to 90 days after the order has been placed.
   * Only available for Checkout flow.
   */
  Order = 'order',

  /**
   * Payment will be immediately submitted for settlement upon creating a transaction.
   * `sale` can be used as an alias for this value.
   */
  Capture = 'capture',
}

export const useRequestBraintreePayment: RequestPaymentFn = () => {
  const { braintreeAuthorizationKey, braintreeBillingAgreementDescription } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });
  const { checkoutPaymentMethod } = usePaymentContext();
  const isPayPalPayment = isPayPal(checkoutPaymentMethod?.accountIdentifier);
  const isVenmoPayment = isVenmo(checkoutPaymentMethod?.accountIdentifier);
  const isBraintreePayment = isPayPalPayment || isVenmoPayment;

  const { braintreeInstances } = useBraintreeInitialization({
    isBraintreePayment,
    isPayPalPayment,
    isVenmoPayment,
    braintreeAuthorizationKey,
  });

  const requestPayment = useCallback(async () => {
    if (!isBraintreePayment || !braintreeInstances.client) {
      return;
    }

    if (isPayPalPayment && braintreeInstances.paypalCheckout) {
      try {
        const paymentData = {
          flow: FlowType.Vault,
          currency: 'USD',
          intent: Intent.Capture,
          requestBillingAgreement: true,
          billingAgreementDescription: braintreeBillingAgreementDescription,
          enableShippingAddress: false,
          shippingAddressEditable: false,
        };

        const payload = await braintreeInstances.paypalCheckout.tokenize(paymentData);
        const deviceData = JSON.parse(braintreeInstances.dataCollector.deviceData).correlation_id;
        const details = JSON.stringify(payload.details);
        const nonce = payload.nonce;

        return {
          paypalDetails: {
            nonce,
            deviceData,
            details,
          },
        };
      } catch (e) {
        if (e && typeof e === 'object' && 'code' in e && e.code === 'PAYPAL_POPUP_CLOSED') {
          return undefined;
        }
        logger.error({
          isPaymentLog: true,
          message:
            '[useRequestBraintreePayment][requestPayment] Error occurred while requesting Paypal payment.',
          isPayPalPayment,
          isVenmoPayment,
          e,
        });
        throw new PaymentRequestError(paymentErrorType.genericError);
      }
    }

    if (isVenmoPayment && braintreeInstances.venmo) {
      try {
        const payload = await braintreeInstances.venmo.tokenize();
        const deviceData = JSON.parse(braintreeInstances.dataCollector.deviceData).correlation_id;
        const details = JSON.stringify(payload.details);
        const nonce = payload.nonce;

        return {
          venmoDetails: {
            nonce,
            deviceData,
            details,
          },
        };
      } catch (e) {
        if (e && typeof e === 'object' && 'code' in e && e.code === 'VENMO_CANCELED') {
          return undefined;
        }
        logger.error({
          isPaymentLog: true,
          message:
            '[useRequestBraintreePayment][requestPayment] Error occurred while requesting Venmo payment.',
          isPayPalPayment,
          isVenmoPayment,
          e,
        });
        throw new PaymentRequestError(paymentErrorType.genericError);
      }
    }

    return undefined;
  }, [
    isBraintreePayment,
    isPayPalPayment,
    isVenmoPayment,
    braintreeInstances,
    braintreeBillingAgreementDescription,
  ]);

  return { requestPayment };
};
